/**
 * Group Chat Context
 * Manages Socket.io connection, messages, reactions, replies, and chat state.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react'
import { io, type Socket } from 'socket.io-client'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// ============================================================
// Types
// ============================================================

export interface ReactionEntry {
  user_id: string
  username: string
  reaction: string
}

export interface ReplyTo {
  id: string
  text: string
  username: string
}

export interface GroupChatMessage {
  id: string
  room_id: string
  user_id: string
  content: string
  message_type: 'text' | 'system' | 'media' | 'admin'
  reply_to_id?: string | null
  reply_to?: ReplyTo | null
  metadata?: Record<string, unknown>
  reactions: ReactionEntry[]
  is_deleted: boolean
  created_at: string
  user: {
    id: string
    chat_username: string
    first_name?: string | null
  }
}

export interface ChatRoom {
  id: string
  name: string
  slug: string
  description: string | null
}

interface GroupChatContextType {
  messages: GroupChatMessage[]
  isConnected: boolean
  isLoading: boolean
  hasAccess: boolean | null
  username: string | null
  needsUsername: boolean
  onlineCount: number
  typingUsers: string[]
  error: string | null
  currentRoom: ChatRoom | null
  hasMoreMessages: boolean
  replyingTo: ReplyTo | null
  sendMessage: (content: string) => void
  sendGif: (gifUrl: string, thumbnailUrl: string, title: string) => void
  setUsername: (username: string) => Promise<{ success: boolean; error?: string }>
  loadMore: () => Promise<void>
  startTyping: () => void
  stopTyping: () => void
  addReaction: (messageId: string, reaction: string) => void
  removeReaction: (messageId: string, reaction: string) => void
  setReplyingTo: (reply: ReplyTo | null) => void
}

const GroupChatContext = createContext<GroupChatContextType | null>(null)

// ============================================================
// Provider
// ============================================================

export function GroupChatProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<GroupChatMessage[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasAccess, setHasAccess] = useState<boolean | null>(null)
  const [username, setUsernameState] = useState<string | null>(null)
  const [needsUsername, setNeedsUsername] = useState(false)
  const [onlineCount, setOnlineCount] = useState(0)
  const [typingUsers, setTypingUsers] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [currentRoom, setCurrentRoom] = useState<ChatRoom | null>(null)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [replyingTo, setReplyingTo] = useState<ReplyTo | null>(null)

  const socketRef = useRef<Socket | null>(null)
  const loadedIdsRef = useRef<Set<string>>(new Set())
  const isLoadingMoreRef = useRef(false)

  const getToken = useCallback(() => localStorage.getItem('auth_token'), [])

  // Check Access on Mount
  useEffect(() => {
    const checkAccess = async () => {
      const token = getToken()
      if (!token) { setHasAccess(false); setIsLoading(false); return }

      try {
        const res = await fetch(`${API_URL}/api/groupchat/access`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) { setHasAccess(false); setIsLoading(false); return }

        const data = await res.json()
        setHasAccess(data.hasAccess)

        if (data.hasAccess && data.isBanned) {
          setError('You have been banned from group chat.')
          setIsLoading(false)
          return
        }
        if (data.hasAccess && !data.username) {
          setNeedsUsername(true)
          setIsLoading(false)
          return
        }
        if (data.hasAccess && data.username) {
          setUsernameState(data.username)
          await loadInitialMessages()
          connectSocket(token, data.username)
        }
      } catch {
        setError('Failed to check chat access')
      } finally {
        setIsLoading(false)
      }
    }
    checkAccess()
    return () => { socketRef.current?.disconnect(); socketRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load Initial Messages
  const loadInitialMessages = async () => {
    const token = getToken()
    if (!token) return
    try {
      const res = await fetch(`${API_URL}/api/groupchat/rooms/general/messages?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const data = await res.json()
      if (data.success && data.messages) {
        setMessages(data.messages)
        data.messages.forEach((m: GroupChatMessage) => loadedIdsRef.current.add(m.id))
        setHasMoreMessages(data.messages.length >= 50)
        if (data.room) setCurrentRoom(data.room)
      }
    } catch (err) {
      console.error('[GroupChat] Failed to load messages:', err)
    }
  }

  // Socket Connection
  const connectSocket = (token: string, _username: string) => {
    if (socketRef.current?.connected) return

    const socket = io(`${API_URL}/groupchat`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 60000,
      forceNew: true,
    })

    socket.on('connect', () => { setIsConnected(true); setError(null) })
    socket.on('disconnect', () => setIsConnected(false))

    socket.on('connect_error', (err) => {
      const msg = err.message
      if (msg === 'GROUP_CHAT_ACCESS_DENIED') { setHasAccess(false); setError('Access denied') }
      else if (msg === 'GROUP_CHAT_BANNED') setError('You have been banned from group chat.')
      else if (msg === 'GROUP_CHAT_USERNAME_REQUIRED') setNeedsUsername(true)
      else setError('Connection failed. Retrying...')
    })

    socket.on('gc:connected', (data: { onlineCount: number }) => setOnlineCount(data.onlineCount))

    socket.on('gc:new_message', (message: GroupChatMessage) => {
      if (!loadedIdsRef.current.has(message.id)) {
        loadedIdsRef.current.add(message.id)
        setMessages((prev) => [...prev, { ...message, reactions: message.reactions || [] }])
      }
    })

    socket.on('gc:user_joined', (data: { onlineCount: number }) => setOnlineCount(data.onlineCount))
    socket.on('gc:user_left', (data: { onlineCount: number }) => setOnlineCount(data.onlineCount))

    socket.on('gc:user_typing', (data: { username: string }) => {
      setTypingUsers((prev) => prev.includes(data.username) ? prev : [...prev, data.username])
    })
    socket.on('gc:user_stop_typing', (data: { username: string }) => {
      setTypingUsers((prev) => prev.filter((u) => u !== data.username))
    })

    // Reaction events
    socket.on('gc:reaction_added', (data: { messageId: string; userId: string; username: string; reaction: string }) => {
      setMessages((prev) => prev.map((m) => {
        if (m.id !== data.messageId) return m
        const exists = m.reactions.some(r => r.user_id === data.userId && r.reaction === data.reaction)
        if (exists) return m
        return { ...m, reactions: [...m.reactions, { user_id: data.userId, username: data.username, reaction: data.reaction }] }
      }))
    })

    socket.on('gc:reaction_removed', (data: { messageId: string; userId: string; reaction: string }) => {
      setMessages((prev) => prev.map((m) => {
        if (m.id !== data.messageId) return m
        return { ...m, reactions: m.reactions.filter(r => !(r.user_id === data.userId && r.reaction === data.reaction)) }
      }))
    })

    socket.on('gc:message_deleted', (data: { messageId: string }) => {
      setMessages((prev) => prev.map((m) =>
        m.id === data.messageId ? { ...m, is_deleted: true, content: '[Message removed by moderator]' } : m
      ))
    })

    socket.on('gc:error', (data: { error: string; code: string }) => {
      if (data.code === 'RATE_LIMITED') {
        setError(data.error)
        setTimeout(() => setError(null), 3000)
      }
    })

    socketRef.current = socket
  }

  // Actions
  const sendMessage = useCallback((content: string) => {
    if (!socketRef.current?.connected || !content.trim()) return
    socketRef.current.emit('gc:send_message', {
      content: content.trim(),
      roomSlug: 'general',
      replyToMessageId: replyingTo?.id || null,
    })
    setReplyingTo(null)
  }, [replyingTo])

  const sendGif = useCallback((gifUrl: string, thumbnailUrl: string, title: string) => {
    if (!socketRef.current?.connected) return
    socketRef.current.emit('gc:send_message', {
      content: title || 'GIF',
      roomSlug: 'general',
      messageType: 'media',
      media: { type: 'gif', url: gifUrl, thumbnail_url: thumbnailUrl, title }
    })
  }, [])

  const setUsername = useCallback(async (newUsername: string) => {
    const token = getToken()
    if (!token) return { success: false, error: 'Not authenticated' }
    try {
      const res = await fetch(`${API_URL}/api/groupchat/username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username: newUsername }),
      })
      const data = await res.json()
      if (!res.ok || data.error) return { success: false, error: data.error || 'Failed' }
      setUsernameState(data.username)
      setNeedsUsername(false)
      await loadInitialMessages()
      connectSocket(token, data.username)
      return { success: true }
    } catch { return { success: false, error: 'Failed to set username' } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken])

  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMoreMessages || messages.length === 0) return
    isLoadingMoreRef.current = true
    const token = getToken()
    if (!token) { isLoadingMoreRef.current = false; return }
    try {
      const oldest = messages[0]
      const res = await fetch(
        `${API_URL}/api/groupchat/rooms/general/messages?before=${oldest.created_at}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (!res.ok) return
      const data = await res.json()
      if (data.success && data.messages) {
        const newMsgs = data.messages.filter((m: GroupChatMessage) => !loadedIdsRef.current.has(m.id))
        newMsgs.forEach((m: GroupChatMessage) => loadedIdsRef.current.add(m.id))
        setMessages((prev) => [...newMsgs, ...prev])
        setHasMoreMessages(data.messages.length >= 50)
      }
    } catch (err) { console.error('[GroupChat] Failed to load more:', err) }
    finally { isLoadingMoreRef.current = false }
  }, [getToken, hasMoreMessages, messages])

  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startTyping = useCallback(() => {
    if (!socketRef.current?.connected) return
    socketRef.current.emit('gc:typing_start', { roomSlug: 'general' })
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      socketRef.current?.emit('gc:typing_stop', { roomSlug: 'general' })
    }, 3000)
  }, [])

  const stopTyping = useCallback(() => {
    if (!socketRef.current?.connected) return
    socketRef.current.emit('gc:typing_stop', { roomSlug: 'general' })
    if (typingTimeoutRef.current) { clearTimeout(typingTimeoutRef.current); typingTimeoutRef.current = null }
  }, [])

  const addReaction = useCallback((messageId: string, reaction: string) => {
    socketRef.current?.emit('gc:add_reaction', { messageId, reaction })
  }, [])

  const removeReaction = useCallback((messageId: string, reaction: string) => {
    socketRef.current?.emit('gc:remove_reaction', { messageId, reaction })
  }, [])

  return (
    <GroupChatContext.Provider value={{
      messages, isConnected, isLoading, hasAccess, username, needsUsername,
      onlineCount, typingUsers, error, currentRoom, hasMoreMessages, replyingTo,
      sendMessage, sendGif, setUsername, loadMore, startTyping, stopTyping,
      addReaction, removeReaction, setReplyingTo,
    }}>
      {children}
    </GroupChatContext.Provider>
  )
}

export function useGroupChat() {
  const context = useContext(GroupChatContext)
  if (!context) throw new Error('useGroupChat must be used within a GroupChatProvider')
  return context
}
